import { useState, useEffect } from "react";
import {
  getExcludedNumbers,
  addExcludedNumber,
  removeExcludedNumber,
} from "@services/smsLeads";
import { sanitizeText } from "@utils/sanitize";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function ExcludedNumbersManager() {
  const [excludedNumbers, setExcludedNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [newPhone, setNewPhone] = useState("");
  const [newReason, setNewReason] = useState("");
  const [adding, setAdding] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  useEffect(() => {
    fetchExcludedNumbers();
  }, [page]);

  const fetchExcludedNumbers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getExcludedNumbers(page, perPage);
      setExcludedNumbers(res.excluded_numbers || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || "Failed to fetch excluded numbers");
    } finally {
      setLoading(false);
    }
  };

  const handleAddExcludedNumber = async (e) => {
    e.preventDefault();
    if (!newPhone.trim()) {
      setError("Phone number is required");
      return;
    }

    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      await addExcludedNumber(sanitizeText(newPhone), sanitizeText(newReason));
      setSuccess(`${newPhone} added to exclusion list`);
      setNewPhone("");
      setNewReason("");
      setPage(1); // Reset to first page to see new entry
      await fetchExcludedNumbers();
    } catch (err) {
      setError(err.message || "Failed to add excluded number");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveExcludedNumber = async (phoneNumber) => {
    if (!window.confirm(`Remove ${phoneNumber} from exclusion list?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await removeExcludedNumber(phoneNumber);
      setSuccess(`${phoneNumber} removed from exclusion list`);
      await fetchExcludedNumbers();
    } catch (err) {
      setError(err.message || "Failed to remove excluded number");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add New Exclusion */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Add Excluded Number
        </h3>
        <form onSubmit={handleAddExcludedNumber} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="tel"
              placeholder="Phone number (e.g., +1234567890)"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={adding}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 font-medium"
          >
            {adding ? "Adding..." : "Add to Exclusion List"}
          </button>
        </form>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Excluded Numbers List */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Excluded Numbers ({total})
        </h3>

        {loading && !excludedNumbers.length ? (
          <div className="text-center text-gray-500 py-8">Loading...</div>
        ) : excludedNumbers.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No excluded numbers yet
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {excludedNumbers.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">
                      {item.phone_number}
                    </div>
                    {item.reason && (
                      <div className="text-sm text-gray-500">{item.reason}</div>
                    )}
                    <div className="text-xs text-gray-400">
                      Added {formatDate(item.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveExcludedNumber(item.phone_number)}
                    disabled={loading}
                    className="ml-4 px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > perPage && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {Math.ceil(total / perPage)}
                </span>
                <button
                  onClick={() =>
                    setPage(Math.min(Math.ceil(total / perPage), page + 1))
                  }
                  disabled={page >= Math.ceil(total / perPage)}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
